<template>
  <modal
    size="lg"
    ref="modal"
    :show="show"
    @hidden="closeModal"
    :scrollable="false"
    :title="title"
    :is-submitting="isSubmitting"
    :hide-footer="true"
    modal-class="create-goal-modal"
    body-class="p-0"
  >
    <goal-editor
      v-if="newGoal"
      :org-id="orgId"
      :team-id="teamId"
      :goal="newGoal"
      :expanded="true"
      :is-submitting="isSubmitting"
      @save-goal="triggerGoalCreation($event)"
      @select-template="onTemplateSelection"
      :error-messages="errorMessages"
      :default-parent-goals="defaultParentGoals"
      :key="goalEditorKey"
      @cancel="closeModal"
    ></goal-editor>
    <max-goals-paywall-modal
      v-model="isPaywallModalShown"
      :org-id="orgId"
      v-if="isPaywallModalShown"
    />
  </modal>
</template>

<script>
import GoalEditor from '@/components/goal/GoalEditor'
import Modal from '@/components/Modal'
import MaxGoalsPaywallModal from '@/components/paywalls/MaxGoalsPaywallModal.vue'
import { featureFlags } from '@/constants/featureFlags'
import CreateGoalMixin from '@/mixins/CreateGoalMixin'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import { mapActions } from 'vuex'

/**
 * This component provides capability to create goal via a Modal
 */
export default {
  name: 'CreateGoalModal',
  props: {
    teamId: Number,
    orgId: Number,
    parentGoal: Object,
    show: Boolean,
    goalMeta: Object,
    title: {
      default: 'Create Goal'
    }
  },
  mixins: [CreateGoalMixin, FeaturesMixin],
  model: {
    prop: 'show'
  },
  components: {
    Modal,
    GoalEditor,
    MaxGoalsPaywallModal
  },
  data() {
    return {
      isSubmitting: false,
      isPaywallModalShown: false,
      isGoalCreationAllowed: true,
      errorMessages: {},
      newGoal: null,
      goalEditorKey: 0,
      defaultParentGoals: [this.parentGoal].filter(Boolean)
    }
  },
  computed: {
    user() {
      return this.$store.getters.user
    }
  },
  methods: {
    ...mapActions(['createNewGoal']),
    showPaywallModal() {
      this.isPaywallModalShown = true
    },
    populateNewGoal() {
      this.newGoal = {
        ...this.newGoalData(this.teamId, this.user.id),
        ...this.goalMeta
      }
    },
    closeModal() {
      this.$emit('input', false)
    },
    async triggerGoalCreation({ parentGoals }) {
      if (!this.isGoalCreationAllowed) {
        this.showPaywallModal()
        return
      }
      const goal = await this.createGoal({ parentGoals, goal: this.newGoal })
      if (goal?.id) {
        this.$refs.modal.hide()
        this.closeModal()
        this.$emit('created', {
          goalId: goal.id,
          parentIds: parentGoals?.map(goal => goal.id)
        })
      }
    },
    onTemplateSelection(goalTemplate) {
      this.newGoal = {
        ...this.prepareGoalFromTemplate(goalTemplate, this.teamId, this.user.id),
        ...this.goalMeta,
        checkinFrequency: this.orgCheckinFrequency
      }
      this.goalEditorKey++
    }
  },
  async mounted() {
    this.populateNewGoal()
    this.fetchCheckinFrequency().then(checkinFrequency => {
      this.newGoal = { ...this.newGoal, checkinFrequency }
    })
    this.isGoalCreationAllowed = await this.satisfyFeature(this.orgId, featureFlags.MAX_GOAL_COUNT)
    if (!this.isGoalCreationAllowed) {
      this.showPaywallModal()
    }
  }
}
</script>

<style lang="scss">
.create-goal-modal .modal-content {
  padding: 0;
}
</style>
